import * as Sentry from '@sentry/react'
import { ViolationType, ViolationsType } from 'shared/violations/types'
import { AxiosError } from 'axios'
import { UpdatedValueDataType, ValueDataType } from 'Types/Tasks.types.ts'
import { EditorEvent } from 'public/tinymce/tinymce'
import { appConfig } from 'Config/app-config'
import { cvPlaymentDomain } from 'Constants/auth.constants'

export const calculateWordCount = (str: string): number => {
    return str?.split(' ').length || 0
}

export const toMinTwoDigits = (num: number): string => {
    return num < 10 ? `0${num}` : `${num}`
}

export const flattenViolation = (violation: ViolationsType) => {
    const allViolation: ViolationType[] = []
    Object.keys(violation).forEach(level => {
        const violationLevel = level as keyof ViolationsType
        Object.values(violation[violationLevel]).forEach(violation => {
            allViolation.push({
                type: violation.type,
                message: violation.message,
                id: violation.id,
            })
        })
    })
    return allViolation
}

export const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
        /**
         * Workaround for browser hard reload
         */
        await navigator.serviceWorker.getRegistration().then(reg => {
            if (reg?.active && !navigator.serviceWorker.controller) {
                window.location.reload()
            }
        })

        return navigator.serviceWorker.register(
            import.meta.env.MODE === 'production' ? '/sw.js' : '/dev-sw.js?dev-sw'
        )
    }
}

// TODO: use this until we have not deprecating the old value_version type,
//       after deprecation the there would be only one type of value_version, so we can remove this.
export const getValueFromOldOrNewValueTypes = (
    data: ValueDataType<'STRING'> | UpdatedValueDataType<'STRING'> | undefined
): string | undefined => {
    return data && 'data' in data ? data.data.value : data?.value
}

export const sanitizeAxiosError = (error: AxiosError): string | undefined => {
    return (
        error.response?.data as { error: { code: number; message: string; meta_data?: object } }
    )?.error?.message
}

export const initialiseSentry = () => {
    Sentry.init({
        dsn: 'https://cc4d8829abb1bc89d6b75438aed4d913@o132323.ingest.us.sentry.io/4507339111268352',
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate: 1.0,
        tracePropagationTargets: [/^https:\/\/playment\.io/],
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        enabled: process.env.NODE_ENV !== 'development',
    })
}

export const convertToSnakeCase = (str: string): string => {
    return str.toLowerCase().replace(/ /g, '_')
}

export const isAlmostEqual = (a: number, b: number, tolerance = 0.5) => Math.abs(a - b) <= tolerance

export const secondsToHoursMinutesSeconds = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return { hours, minutes, remainingSeconds }
}

export const getCountdownTimerText = (hours: number, minutes: number, seconds: number) => {
    return `${toMinTwoDigits(hours)}:${toMinTwoDigits(minutes)}:${toMinTwoDigits(seconds)}`
}

export const secondsToMinutes = (seconds: number) => {
    return Math.floor(seconds / 60)
}

// input format 2024-10-16T06:06:42.848112Z
// output format 10:00 AM, May 23, 2024
export const getFormattedDateTime = (dateTimeString: string) => {
    const dateTime = new Date(dateTimeString)
    const formattedDate = dateTime.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
    const formattedTime = dateTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    })
    return `${formattedTime}, ${formattedDate}`
}

export const formatString = (template: string, values: Record<string, string>) => {
    return template.replace(/{(\w+)}/g, (match, key) => {
        return key in values ? values[key] : match
    })
}

export const formatTimeWithMilliseconds = (seconds: number) => {
    const totalMs = seconds * 1000
    const ms = Math.floor(totalMs % 1000)
    const totalSeconds = Math.floor(seconds)
    const s = totalSeconds % 60
    const m = Math.floor(totalSeconds / 60) % 60
    const h = Math.floor(totalSeconds / 3600)
    return `${h > 0 ? h + ':' : ''}${m.toString().padStart(2, '0')}:${s
        .toString()
        .padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
}

export const getDownloadableUrlFromGoogleDriveLink = (url: string | null) => {
    if (!url) {
        return null
    }
    if (!url.includes('drive.google.com')) {
        return url
    }
    const fileKey = url.split('/d/')[1].split('/')[0]
    return `https://drive.google.com/file/d/${fileKey}/view?usp=drivesdk`
}

export const downloadPublicFile = async (url: string) => {
    try {
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error('Network response was not ok')
        }
        const blob = await response.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = downloadUrl
        a.download = url.split('/').pop() ?? ''
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(downloadUrl)
    } catch (e) {
        console.error('Error downloading file:', e)
    }
}

export const handlePasteFunctionality = (
    e: EditorEvent<ClipboardEvent> | ClipboardEvent,
    isPasteFunctionalityEnabled: boolean,
    showError: (content: string) => void
) => {
    if (isPasteFunctionalityEnabled) {
        return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clipboardData = e.clipboardData || (window as any).clipboardData
    const items = clipboardData?.items

    if (items) {
        let containsImage = false
        for (const item of items) {
            if (item.type.startsWith('image/')) {
                containsImage = true
                break
            }
        }

        if (!containsImage) {
            showError('Pasting in the editor is disabled.')
            e.preventDefault()
        }
    }
}

export const getUrlParameter = (name: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search)
    const value = urlParams.get(name)

    return value
}

export const setCookie = (name: string, value: string, domain: string, days: number = 14) => {
    const expires = new Date()

    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
    const cookieString = `${name}=${value}; expires=${expires.toUTCString()}; domain=${domain}; path=/; SameSite=Lax`

    document.cookie = cookieString
}

export const redirectFromCvToAiDomain = () => {
    let currentFtsAppUrl: URL
    let ftsAppAiUrl: URL

    try {
        currentFtsAppUrl = new URL(window.location.href)
        ftsAppAiUrl = new URL(appConfig.ftsClientBaseUrlAi)
    } catch (error) {
        console.error('Invalid URL', error)
        return
    }

    if (!currentFtsAppUrl.host.includes(cvPlaymentDomain)) {
        return
    }

    const finalUrl = new URL(currentFtsAppUrl)
    finalUrl.host = ftsAppAiUrl.host

    window.location.replace(finalUrl)
}

/**
 * Finds the last complete JSON object in a buffer string
 * Returns the JSON string and the remaining buffer, or null if no complete JSON found
 */
export const findLastCompleteJSON = (
    buffer: string
): { jsonStr: string; remainingBuffer: string } | null => {
    let braceCount = 0
    let inString = false
    let escapeNext = false
    let jsonStartIndex = -1
    let lastJsonStartIndex = -1
    let lastJsonEndIndex = -1

    // Find the start and end of the last complete JSON object
    // This ensures we process the most recent/up-to-date data in streaming scenarios
    for (let i = 0; i < buffer.length; i++) {
        const char = buffer[i]

        if (escapeNext) {
            escapeNext = false
            continue
        }

        if (char === '\\' && inString) {
            escapeNext = true
            continue
        }

        if (char === '"') {
            inString = !inString
            continue
        }

        if (!inString) {
            if (char === '{') {
                if (braceCount === 0) {
                    jsonStartIndex = i
                }
                braceCount++
            } else if (char === '}') {
                braceCount--
                if (braceCount === 0 && jsonStartIndex >= 0) {
                    lastJsonStartIndex = jsonStartIndex
                    lastJsonEndIndex = i + 1
                }
            }
        }
    }

    if (lastJsonEndIndex > 0 && lastJsonStartIndex >= 0) {
        const jsonStr = buffer.substring(lastJsonStartIndex, lastJsonEndIndex)
        const remainingBuffer = buffer.substring(lastJsonEndIndex).trimStart()
        return { jsonStr, remainingBuffer }
    }

    return null
}
